export const DISPATCH_CAPTURE_ARRIVAL_POLL_INTERVAL_MS = 15_000;
export const DISPATCH_CAPTURE_ARRIVAL_HIGHLIGHT_MS = 6_300;
export const DISPATCH_CAPTURE_ARRIVAL_CLASS_NAME = "motion-safe:animate-dispatch-capture-arrival-blink";
export const DISPATCH_CAPTURE_UNREAD_CLASS_NAME = "motion-safe:animate-dispatch-capture-unread-blink";

const pendingNewestCountByView = {
  errors: 0,
  reassignment: 0,
  source_changes: 0,
  consolidation: 0,
  reports: 0,
  journal: 0,
};

function normalizeCaptureKey(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeKeyList(keys) {
  const seen = new Set();
  const result = [];
  for (const value of Array.isArray(keys) ? keys : []) {
    const key = normalizeCaptureKey(value);
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(key);
  }
  return result;
}

function normalizeCount(count) {
  const numericCount = Number(count);
  if (!Number.isFinite(numericCount) || numericCount < 0) {
    return null;
  }
  return Math.trunc(numericCount);
}

function isCaptureView(view) {
  return Object.hasOwn(pendingNewestCountByView, view);
}

export function buildAlertCaptureKey(capture) {
  return normalizeCaptureKey(capture?.id);
}

export function buildOrderImportCaptureKey(item) {
  return normalizeCaptureKey(item?.audit_id);
}

export function buildReportActionCaptureKey(item) {
  return normalizeCaptureKey(item?.audit_id);
}

export function buildReassignmentCaptureKey(item) {
  const operationEventId = normalizeCaptureKey(item?.operationEventId);
  if (operationEventId) {
    return `operation|${operationEventId}`;
  }
  const movementEventId = normalizeCaptureKey(item?.diagnostics?.movement_event_id);
  if (movementEventId) return `movement|${movementEventId}`;
  return [
    normalizeCaptureKey(item?.recordedAt),
    normalizeCaptureKey(item?.action),
    normalizeCaptureKey(item?.actorUserId),
    normalizeCaptureKey(item?.status),
    item?.totalMs == null || !Number.isFinite(Number(item.totalMs)) ? "" : String(item.totalMs),
  ].join("|");
}

export function takeNewestCaptureKeys(keys, count) {
  const normalizedCount = normalizeCount(count);
  if (normalizedCount == null || normalizedCount <= 0) {
    return [];
  }
  return normalizeKeyList(keys).slice(0, normalizedCount);
}

export function addPendingNewestCaptureCount(view, delta) {
  if (!isCaptureView(view)) {
    return;
  }
  const normalizedDelta = normalizeCount(delta);
  if (normalizedDelta == null || normalizedDelta <= 0) {
    return;
  }
  pendingNewestCountByView[view] += normalizedDelta;
}

export function consumePendingNewestCaptureCount(view) {
  if (!isCaptureView(view)) {
    return 0;
  }
  const pendingCount = pendingNewestCountByView[view];
  pendingNewestCountByView[view] = 0;
  return pendingCount;
}

export function resetDispatchCaptureArrivalState() {
  for (const view of Object.keys(pendingNewestCountByView)) pendingNewestCountByView[view] = 0;
}

export function createDispatchCaptureKeyTracker() {
  let seenKeys = null;

  return {
    ingest(keys) {
      const nextKeys = normalizeKeyList(keys);
      if (seenKeys == null) {
        seenKeys = new Set(nextKeys);
        return [];
      }

      const arrived = nextKeys.filter((key) => !seenKeys.has(key));
      if (nextKeys.length < seenKeys.size && arrived.length === 0) {
        seenKeys = new Set(nextKeys);
        return [];
      }
      if (
        arrived.length > 0
        && arrived.length === nextKeys.length
        && nextKeys.length < seenKeys.size
      ) {
        seenKeys = new Set(nextKeys);
        return [];
      }

      for (const key of nextKeys) {
        seenKeys.add(key);
      }
      return arrived;
    },
  };
}

export function createDispatchCaptureCountTracker() {
  let baseline = null;

  return {
    ingest(count) {
      const nextCount = normalizeCount(count);
      if (nextCount == null) {
        return 0;
      }
      if (baseline == null) {
        baseline = nextCount;
        return 0;
      }
      if (nextCount < baseline) {
        baseline = nextCount;
        return 0;
      }
      const delta = nextCount - baseline;
      baseline = nextCount;
      return delta;
    },
  };
}
